import { isEmpty } from 'lodash';
import { configuratorBridge } from '../unity/unityMethods';

/**
 * The transform of every prop group of a scene, kept against the view it belongs to.
 *
 * A prop group is an empty "frame" object with the props of the group standing loose beside it, not under it. Unity
 * hands the props of a group their transform one at a time when a view is loaded (ConfiguratorMainController.
 * UpdateConfiguratorPropsInfoReceiver), and the frame is not among them - a group has no entry in the transform data
 * kept against a view. Unity works the frame out again from the props instead, in RoomData.
 * RefreshGroupTransformsFromProps().
 *
 * That only works while the group is scaled evenly. A scale read back off a prop one axis at a time means nothing once
 * the prop is turned - lossyScale is an approximation across a turn and dividing it axis by axis hands back a scale
 * the group never had - so TryReadGroupTransformFromProps() refuses to read it and leaves the frame alone. Scale one
 * axis of a group and turn it about y, which is the plainest thing to do to a group, and switching views leaves the
 * frame carrying the values of the view before while the props stand correctly in the new one. The scene is right and
 * every number the transformation panel shows is wrong.
 *
 * So the frame transform is kept here, per view, and put back on the group when the view is loaded rather than being
 * guessed at.
 */

const RAD2DEG = 180 / Math.PI;

/**
 * Whether a view's transforms are being handed to unity right this moment.
 *
 * Unity answers a load by reporting a transform change: UpdateConfiguratorPropsInfoReceiver ends on
 * transformGizmo.transformChanged(), which reaches react as ReceiveConfiguratorTransformValueChanged, and
 * useSaveSceneCallbacks answers that by saving the transforms of the scene as they now stand against the view which is
 * selected. So loading a view saves it straight back, and while a switch is still going through, "the view which is
 * selected" can still be the one being left - which writes the transforms of the view being opened onto the view being
 * closed. Both views end up carrying the same numbers while their props keep the places they were each given, which is
 * a panel showing one set of values for two views that plainly do not match.
 *
 * A load must never cause a save, so the callbacks stand down while this is set. Unity calls back into javascript
 * inside the SendMessage itself, so the flag is set for the whole of that call and is read while it is still set.
 */
let applyingViewTransformsDepth = 0;

export const isApplyingViewTransforms = () => applyingViewTransformsDepth > 0;

export const runWhileApplyingViewTransforms = (apply) => {
  applyingViewTransformsDepth += 1;
  try {
    return apply();
  } finally {
    // Let go a turn later, not here. PubSub does not hand an event to its subscribers as it is published - it queues
    // them with setTimeout(deliver, 0), see pubsub-js/src/pubsub.js - so the callbacks which have to stand down have
    // not run yet at this point. Letting go here left the flag clear by the time they arrived, which made the whole
    // guard do nothing. Unity publishes while apply() is still running, so its delivery is queued ahead of this and
    // runs first.
    setTimeout(() => {
      applyingViewTransformsDepth -= 1;
    }, 0);
  }
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const toPositiveDegrees = (radians) => {
  const degrees = (radians * RAD2DEG) % 360;
  return degrees < 0 ? degrees + 360 : degrees;
};

const numberOr = (value, fallback) => {
  const asNumber = Number(value);
  return Number.isFinite(asNumber) ? asNumber : fallback;
};

/**
 * The euler angles unity would show for a quaternion. Unity turns around z, then x, then y, so the rotation is
 * Ry * Rx * Rz and the angles have to be taken back out in that order - taking them out in any other order hands back
 * angles which describe a different turn. The saved transform of a group keeps its rotation as a quaternion while the
 * panel and UpdateSelectedTransform both work in euler angles, so this is what joins the two.
 *
 * Checked against the other direction over a spread of angles: turning these angles back into a quaternion gives the
 * one that came in, to the last digit, including the case where unity folds 90,90,90 down to 90,0,0.
 */
export const quaternionToUnityEuler = (x, y, z, w) => {
  const r02 = 2 * (x * z + w * y);
  const r12 = 2 * (y * z - w * x);
  const r22 = 1 - 2 * (x * x + y * y);
  const r10 = 2 * (x * y + w * z);
  const r11 = 1 - 2 * (x * x + z * z);

  const sinX = clamp(-r12, -1, 1);
  const angleX = Math.asin(sinX);

  // Straight up or straight down: y and z turn about the same line and only their sum means anything, so it is all
  // put on y. Reading them separately here hands back two angles which fight each other.
  if (Math.abs(sinX) > 0.9999) {
    return {
      x: toPositiveDegrees(angleX),
      y: toPositiveDegrees(Math.atan2(-2 * (x * z - w * y), 1 - 2 * (y * y + z * z))),
      z: 0,
    };
  }

  return {
    x: toPositiveDegrees(angleX),
    y: toPositiveDegrees(Math.atan2(r02, r22)),
    z: toPositiveDegrees(Math.atan2(r10, r11)),
  };
};

/**
 * The saved transform of every prop group of a groupPropsMapping, against the name of the group it belongs to, exactly
 * as unity wrote it (RoomData.GetGroupTransformDataForSave()). A group which was never written out is left out: that is
 * what an all zero scale means, no group stands at a scale of zero, see RoomData.IsGroupTransformDataUnset().
 */
export const readGroupTransformDataByName = (groupPropsMapping) => {
  const transformDataByName = {};

  Object.values(groupPropsMapping || {}).forEach((groupPropData) => {
    Object.values(groupPropData?.groupData || {}).forEach((groupData) => {
      const transformData = groupData?.transformChangedObjectData;
      const groupObjectName = groupData?.groupObjectName;

      if (!groupObjectName || !transformData) {
        return;
      }

      if (!transformData.xScale && !transformData.yScale && !transformData.zScale) {
        return;
      }

      transformDataByName[groupObjectName] = transformData;
    });
  });

  return transformDataByName;
};

/**
 * Picks the transform of every prop group out of a scene json, in the shape UpdateSelectedTransform takes.
 * Unity writes these on every save from the frame as it stands right then, see
 * ConfiguratorController.GetConfiguratorJson() and RoomData.GetGroupTransformDataForSave().
 */
export const readGroupTransformsFromSceneJson = (sceneJson) => {
  const groupTransforms = {};

  Object.entries(readGroupTransformDataByName(sceneJson?.groupPropsMapping)).forEach(
    ([groupObjectName, transformData]) => {
      groupTransforms[groupObjectName] = {
        position: {
          x: numberOr(transformData.xPosition, 0),
          y: numberOr(transformData.yPosition, 0),
          z: numberOr(transformData.zPosition, 0),
        },
        rotation: quaternionToUnityEuler(
          numberOr(transformData.xRotation, 0),
          numberOr(transformData.yRotation, 0),
          numberOr(transformData.zRotation, 0),
          numberOr(transformData.wRotation, 1)
        ),
        scale: {
          x: numberOr(transformData.xScale, 0),
          y: numberOr(transformData.yScale, 0),
          z: numberOr(transformData.zScale, 0),
        },
      };
    }
  );

  return groupTransforms;
};

/**
 * Puts the transform every prop group came back from the curator with onto the mapping the scene already holds.
 *
 * Only the transform of a group travels back from the curator (RoomController.GetSeparatedRoomInfo). Everything else the
 * configurator keeps against a group - the tuft buttons, the cutaways, the product builder data, which prop of it a
 * label points at - is only ever written by the configurator, so the mapping of the scene is kept and the transforms are
 * merged into it by group name. Taking the mapping which came back instead threw all of that away, and it emptied the
 * groups of the scene outright whenever nothing came back with it.
 *
 * @param {object} sceneMapping The groupPropsMapping of the scene json.
 * @param {object} curatorMapping The groupPropsMapping the curator handed over.
 */
export const mergeGroupTransformsIntoSceneMapping = (sceneMapping, curatorMapping) => {
  const transformDataByName = readGroupTransformDataByName(curatorMapping);

  if (isEmpty(transformDataByName)) {
    return sceneMapping;
  }

  // A scene holding no groups while the curator hands some over is a scene whose groups were emptied by the assignment
  // this replaces - the curator only ever knows the groups the scene sent it - so what came back is taken as it is and
  // the groups of the scene are whole again. A group of the lightroom which the scene never had cannot survive this
  // either way: none of its props is a prop of the configurator scene, so it is skipped on load
  // (ConfiguratorController.ReadGroupPropsMappingForConfigurator).
  if (isEmpty(sceneMapping)) {
    return curatorMapping;
  }

  const mergedMapping = {};

  Object.entries(sceneMapping).forEach(([groupPropKey, groupPropData]) => {
    const groupDataMapping = groupPropData?.groupData;

    if (!groupDataMapping) {
      mergedMapping[groupPropKey] = groupPropData;
      return;
    }

    const mergedGroupData = {};

    Object.entries(groupDataMapping).forEach(([groupKey, groupData]) => {
      const transformData = transformDataByName[groupData?.groupObjectName];

      mergedGroupData[groupKey] = transformData
        ? { ...groupData, transformChangedObjectData: transformData }
        : groupData;
    });

    mergedMapping[groupPropKey] = { ...groupPropData, groupData: mergedGroupData };
  });

  return mergedMapping;
};

/**
 * Puts the frame transform of every group of a view back on it.
 *
 * MUST be sent before the transforms of the individual props of the view, never after. Setting the frame carries the
 * props of the group with it - that is what UpdateSelectedTransform does for a group name, and there is no way from
 * here to move the frame on its own - so the props are left somewhere meaningless by this. Sending the props next puts
 * every one of them on an outright value and undoes that completely. The other way round, the props would be moved off
 * their view a second time by the frame and the whole group would be thrown across the scene.
 *
 * @param {object} groupTransforms What readGroupTransformsFromSceneJson() took down for this view.
 */
export const applyGroupTransformsToUnity = (groupTransforms) => {
  if (isEmpty(groupTransforms)) {
    return;
  }

  Object.entries(groupTransforms).forEach(([groupObjectName, transform]) => {
    if (!transform) {
      return;
    }

    // Unity resolves a group name to the parent object of that group on its own and does not read the selection for
    // it, see ConfiguratorTransformController.GetGroupParent(), so the group does not have to be selected for this.
    configuratorBridge.UpdateSelectedTransform({
      propertyName: 'Position',
      axes: transform.position,
      objectId: groupObjectName,
    });
    configuratorBridge.UpdateSelectedTransform({
      propertyName: 'Rotation',
      axes: transform.rotation,
      objectId: groupObjectName,
    });
    configuratorBridge.UpdateSelectedTransform({
      propertyName: 'Scale',
      axes: transform.scale,
      objectId: groupObjectName,
    });
  });
};
